import apollo from '@/graphql'

import SaveUserDetails from '@/graphql/user/SaveUserDetails.gql'
import UserDetailsQuery from '@/graphql/user/UserDetailsQuery.gql'
import SendRegistrationLink from '@/graphql/user/SendRegistrationLink.gql'
import DeleteUser from '@/graphql/user/DeleteUser.gql'
import RestoreUser from '@/graphql/user/RestoreUser.gql'

// initial state
const state = {
  editableUserFields: {},
  userDetails: {
    company: {},
    roles: [],
    stores: [],
  },
  isUserDetailsLoading: false,
}

// getters
const getters = {
  editableUserFields: st => st.editableUserFields,
  userDetails: st => st.userDetails,
  isUserDetailsLoading: st => st.isUserDetailsLoading,
}

// mutations
const mutations = {
  createUserDetails() {
    state.userDetails = {
      company: {},
      roles: [],
      stores: [],
    }
    state.editableUserFields = {
      companiesId: undefined,
      isSendIntro: true,
      isSendRetailerOnboarding: false,
      notifications: [],
      role: null,
      storesId: undefined,
      stores: [],
    }
  },

  setEditableUserFields(state, userDetails) {
    state.editableUserFields = {
      companiesId: userDetails.companiesId,
      companyType: userDetails.company.companyTypesId,
      email: userDetails.email,
      id: userDetails.id,
      isSendIntro: userDetails.isSendIntro,
      isSendRetailerOnboarding: !!userDetails.notifications.find(notification =>
        // 7 is one of the two notification ids for onboarding emails
        notification.notificationsId === 7,
      ),
      jobTitle: userDetails.jobTitle,
      name: userDetails.name,
      notifications: userDetails.notifications.map(notification => ({
        storesId: notification.storesId,
        notificationsId: notification.notificationsId,
      })),
      phone: userDetails.phone,
      role: userDetails.role,
      storesId: userDetails.storesId,
      stores: userDetails.stores,
    }
  },

  setUserDetails(state, userDetails) {
    state.userDetails = userDetails
  },

  setUserDetailsLoading(state, value) {
    state.isUserDetailsLoading = value
  },
}

// actions
const actions = {
  loadUserDetails({ commit }, { id }) {
    commit('setUserDetailsLoading', true)
    apollo.defaultClient
      .query({
        query: UserDetailsQuery,
        variables: { id: +id },
      })
      .then(({ data: { userDetails } }) => {
        commit('setEditableUserFields', userDetails)
        commit('setUserDetails', userDetails)
        commit('setUserDetailsLoading', false)
      })
  },

  resetUserDetails({ commit, state }) {
    commit('setEditableUserFields', state.userDetails)
  },

  saveUserDetails({ commit }, { $message, $router }) {
    commit('setUserDetailsLoading', true)
    apollo.defaultClient.mutate({
      mutation: SaveUserDetails,
      variables: { fields: state.editableUserFields },
      refetchQueries: [state.userDetails.id].filter(id => id).map(id => ({
        query: UserDetailsQuery,
        variables: { id },
      })),
    }).then(({ data: { saveUserDetails: { id } } }) => {
      commit('setUserDetailsLoading', false)
      $message({ message: 'Changes saved', type: 'success', center: false })
      $router.push(`/user/${id}`).catch((err) => {
        if (err.name !== 'NavigationDuplicated') {
          throw err
        }
      })
    }).catch((err) => {
      $message.error(err.message)
      commit('setUserDetailsLoading', false)
    })
  },

  sendRegistrationLink(context, { $message, email }) {
    apollo.defaultClient
      .mutate({
        mutation: SendRegistrationLink,
        variables: { email },
      })
      .then(({ data: { sendRegistrationLink } }) => {
        if (sendRegistrationLink) {
          $message({
            message: 'Invite resent',
            type: 'success',
            center: false,
          })
          return
        }
        // Unsure how this would ever happen given return values
        $message({
          message: 'Invite failed to resend',
          type: 'error',
          center: false,
        })
      })
      .catch(() => {
        $message({
          message: 'Invite failed to resend',
          type: 'error',
          center: false,
        })
      })
  },

  deleteUser({ state }, { $message, $router }) {
    apollo.defaultClient
      .mutate({
        mutation: DeleteUser,
        variables: { id: state.userDetails.id },
        refetchQueries: [state.userDetails.id]
          .filter(id => id)
          .map(id => ({
            query: UserDetailsQuery,
            variables: { id },
          })),
      })
      .then(() => {
        $message({
          message: 'User marked as Deleted',
          type: 'success',
          center: false,
        })
        $router.push(`/user/${state.userDetails.id}`)
      })
  },

  restoreUser({ state }, { $message, $router }) {
    apollo.defaultClient
      .mutate({
        mutation: RestoreUser,
        variables: { id: state.userDetails.id },
        refetchQueries: [state.userDetails.id]
          .filter(id => id)
          .map(id => ({
            query: UserDetailsQuery,
            variables: { id },
          })),
      })
      .then(() => {
        $message({
          message: 'User marked as Restored',
          type: 'success',
          center: false,
        })
        $router.push(`/user/${state.userDetails.id}`)
      })
  },
}

export default {
  state,
  getters,
  mutations,
  actions,
}



// WEBPACK FOOTER //
// ./src/store/modules/user/UserDetails.js